<!-- src/components/ri3/department/DepartmentEditor.vue -->
<template>
  <avs-card
    class="department-editor-card"
    :style="{ minHeight: $vuetify.breakpoint.smAndDown ? '100vh' : '60vh' }"
  >
    <template v-slot:title>
      <v-card-title class="pl-0">
        {{ $t("commons.department") }}
      </v-card-title>
    </template>

    <template v-slot:content>
      <v-form
        ref="form"
        :readonly="!isEditable"
        class="department-form p-4"
      >
        <!-- ===== BODY : champs + info parent ===== -->
        <div class="form-body">
          <!-- Nom -->
          <div class="form-field">
            <v-text-field
              v-model="department.name"
              :rules="rules.name"
              :label="$t('commons.name')"
              counter="64"
              maxlength="64"
              outlined
              dense
            />
          </div>

          <!-- INFO : Sous-départements du département de tutelle sélectionné -->
          <div
            v-if="selectedParentChildren.length"
            class="form-field"
          >
            <v-alert
              type="info"
              color="secondary"
              outlined
              dense
              class="mb-1"
            >
              <div class="font-weight-bold mb-1">
                Ce département de tutelle possède déjà
                {{ selectedParentChildren.length }} sous-département(s) :
              </div>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="child in selectedParentChildren"
                  :key="child.id"
                  small
                  label
                  class="ma-1"
                >
                  {{ child.name }}
                </v-chip>
              </div>
            </v-alert>
          </div>

          <!-- Département de tutelle -->
          <div class="form-field">
            <v-select
              v-model="department.parent"
              :items="parents"
              :rules="rules.parent"
              item-text="name"
              item-value="id"
              :label="$t('Departement de tutelle')"
              clearable
              attach
              outlined
              dense
            ></v-select>
          </div>

          <!-- Budget (affiché avec séparateur de milliers, envoyé en Number) -->
          <div class="form-field">
            <v-text-field
              v-model="budgetDisplay"
              :rules="rules.budget"
              dense
              outlined
              :label="$t('Budget')"
              prefix="XOF "
            />
          </div>

          <!-- Fréquence -->
          <div class="form-field">
            <v-select
              v-model="department.frequency"
              :items="frequencyItems"
              :rules="rules.frequency"
              dense
              outlined
              clearable
              :label="$t('Frequence')"
            ></v-select>
          </div>

          <!-- Description -->
          <div class="form-field">
            <v-textarea
              v-model="department.description"
              :rules="rules.description"
              :label="$t('commons.description')"
              outlined
              dense
              auto-grow
            ></v-textarea>
          </div>
        </div>

        <!-- ===== ACTIONS : toujours en bas ===== -->
        <div class="form-actions">
          <avs-saveButton
            @click="save"
            :disabled="!isEditable"
          />
          <avs-cancelButton
            @click="cancel"
          />
        </div>
      </v-form>
    </template>
  </avs-card>
</template>

<script>
import { DepartmentService } from "@/services"
import { isEqual } from "@/utils/object"
import { required, slug, max64 } from "@/utils/validator" // on ne prend plus isNumber ici
import { FREQUENCY_ITEMS } from "@/utils/consts"
import _ from "lodash"

export default {
  name: "DepartmentEditor",
  props: {
    hasUniqueName: {
      type: Function,
      required: true,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
    // v-model
    value: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      department: this.initDepartment(this.value),
      parents: [],
      rules: {
        description: [],
        // on remplace isNumber par une règle maison qui accepte "1 000 000"
        budget: [this.budgetRule],
        code: [required, slug, max64],
        name: [required, max64, this.validateUniqueName],
        frequency: [],
        parent: [],
      },
    }
  },
  computed: {
    company() {
      return this.$store.state.app.company
    },
    frequencyItems() {
      return FREQUENCY_ITEMS
    },
    // ID normalisé du parent sélectionné
    selectedParentId() {
      const p = this.department && this.department.parent
      if (!p) return null
      return typeof p === "object" ? p.id : p
    },
    // Sous-départements du parent sélectionné
    selectedParentChildren() {
      const parentId = this.selectedParentId
      if (!parentId || !Array.isArray(this.parents)) return []
      return this.parents.filter(dep => {
        const depParent = typeof dep.parent === "object" ? dep.parent?.id : dep.parent
        return depParent === parentId
      })
    },
    /**
     * v-model utilisé par le champ Budget :
     * - GET  -> formate avec séparateur de milliers
     * - SET  -> parse en Number et stocke dans department.budget
     */
    budgetDisplay: {
      get() {
        return this.formatBudget(this.department.budget)
      },
      set(v) {
        this.department.budget = this.parseBudget(v)
      },
    },
  },
  watch: {
    // Quand on change de département à éditer dans la modale
    value: {
      immediate: true,
      handler(val) {
        this.department = this.initDepartment(val)
      },
    },
  },
  created() {
    this.fetchParents()
  },
  methods: {
    initDepartment(val) {
      if (val && Object.keys(val).length) {
        return _.cloneDeep(val)
      }
      return {
        id: null,
        name: "",
        description: "",
        budget: null,
        parent: null,
        frequency: null,
      }
    },
    validateUniqueName(v) {
      if (!v) return true
      if (!this.hasUniqueName) return true
      const result = this.hasUniqueName(v)
      return result === true || result === undefined || result === null ? true : result
    },

    // ------- Parsing / format budget --------
    parseBudget(value) {
      if (value === null || value === undefined || value === "") return null
      if (typeof value === "number") return value
      const cleaned = String(value)
        .replace(/\s/g, "")   // supprime les espaces
        .replace(/,/g, ".")   // virgule → point
      const n = Number(cleaned)
      if (Number.isNaN(n)) return null
      return n
    },
    formatBudget(value) {
      if (value === null || value === undefined || value === "") return ""
      const n = typeof value === "number" ? value : this.parseBudget(value)
      if (n === null) return String(value) // si on n'arrive pas à parser, on laisse brut
      // Séparateur de milliers façon FR : "1 234 567"
      return n.toLocaleString("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    },
    budgetRule(v) {
      if (v === null || v === undefined || v === "") return true
      const n = this.parseBudget(v)
      if (n === null) {
        return "Veuillez saisir un nombre valide."
      }
      if (n < 0) {
        return "Le budget ne peut pas être négatif."
      }
      return true
    },
    // ------- Actions --------
    cancel() {
      this.$emit("cancel")
    },
    async performSave(payload) {
      // On s’assure que le budget envoyé est bien un Number
      payload.budget = this.parseBudget(this.department.budget)

      if (payload && payload.id) {
        // UPDATE
        return DepartmentService.put(payload)
      }
      // CREATE
      return DepartmentService.post(this, this.company.pk, payload)
    },
    fetchParents() {
      DepartmentService.getDepartment(this.company.pk)
        .then(response => {
          this.parents = response || []
        })
        .catch(() => {
          this.parents = []
        })
    },
    async save() {
      const form = this.$refs.form
      if (!form || !form.validate || !form.validate()) return

      // Si rien n'a changé => pas d'appel API
      if (isEqual(this.department, this.value || {})) {
        this.$emit("success")
        return
      }

      try {
        const payload = _.cloneDeep(this.department)
        const response = await this.performSave(payload)
        this.$emit("input", response) // v-model
        this.$emit("success")
      } catch (error) {
        const message =
          error?.response?.data?.detail ||
          error?.response?.data?.non_field_errors?.[0] ||
          error.message ||
          this.$t("messages.error.operation_failed")
        this.$emit("error", message)
      }
    },
  },
}
</script>

<style scoped>
/* Le card remplit la modale, avec padding confortable sur le contenu */
.department-editor-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* On augmente le padding interne de la zone content de la carte */
.department-editor-card .v-card__text {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 24px 32px 20px; /* marge par rapport au bord du modal */
}

/* Le form remplit la hauteur et répartit body / actions */
.department-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 15px !important;
}

/* Partie scrollable avec les champs */
.form-body {
  flex: 1;
  overflow-y: auto;
}

/* Chaque bloc de champ a une marge verticale pour éviter l'effet collé */
.form-field {
  margin-bottom: 12px;
}

/* Boutons collés en bas, alignés à droite */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}
</style>
